import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, Transition, TransitionChild, DialogPanel, DialogTitle } from '@headlessui/react';
import { FiX } from 'react-icons/fi';

const SIZES = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-lg',
  lg: 'sm:max-w-2xl',
  xl: 'sm:max-w-4xl',
};

/**
 * Shared drawer: side panel on sm+ screens, full-screen sheet on phones.
 * Headless UI Dialog provides focus trap, scroll lock, ESC and aria wiring.
 *
 * Props:
 * - open: boolean to control visibility
 * - onClose: callback function when closing
 * - title?: optional header title
 * - footer?: optional footer node (sticky at bottom)
 * - size?: 'sm' | 'md' | 'lg' | 'xl' (translates to desktop widths max-w-md/-lg/-2xl/-4xl)
 * - side?: 'start' | 'end' (logical alignment, default 'end')
 * - dismissible?: default true (shows close X in header)
 * - closeOnBackdrop?: default false (determines if ESC or clicking backdrop closes drawer)
 * - children: main panel content
 */
export default function Drawer({
  open,
  onClose,
  title,
  footer,
  size = 'md',
  side = 'end',
  dismissible = true,
  closeOnBackdrop = false,
  saving = false,
  children,
}) {
  const { t } = useTranslation();

  // Sliding transitions based on logical side
  const transitionClasses = side === 'end'
    ? {
        enterFrom: 'translate-x-full rtl:-translate-x-full',
        enterTo: 'translate-x-0',
        leaveFrom: 'translate-x-0',
        leaveTo: 'translate-x-full rtl:-translate-x-full',
      }
    : {
        enterFrom: '-translate-x-full rtl:translate-x-full',
        enterTo: 'translate-x-0',
        leaveFrom: 'translate-x-0',
        leaveTo: '-translate-x-full rtl:translate-x-full',
      };

  const sideClass = side === 'start' ? 'start-0' : 'end-0';

  return (
    <Transition show={!!open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={!saving && dismissible && closeOnBackdrop ? onClose : () => {}}
      >
        {/* Backdrop overlay */}
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/50 dark:bg-black/60 backdrop-blur-sm" />
        </TransitionChild>

        {/* Drawer container */}
        <div className="fixed inset-0 overflow-hidden">
          <div className={`fixed inset-y-0 ${sideClass} flex max-w-full`}>
            <TransitionChild
              as={Fragment}
              enter="transform transition ease-in-out duration-300"
              enterFrom={transitionClasses.enterFrom}
              enterTo={transitionClasses.enterTo}
              leave="transform transition ease-in-out duration-300"
              leaveFrom={transitionClasses.leaveFrom}
              leaveTo={transitionClasses.leaveTo}
            >
              <DialogPanel
                className={`w-screen ${SIZES[size] || SIZES.md} bg-surface text-content shadow-xl
                  border-x border-edge flex flex-col h-[100dvh]
                  pt-safe pb-[env(safe-area-inset-bottom)]`}
              >
                {/* Fixed Header */}
                {(title || dismissible) && (
                  <div className="shrink-0 px-4 sm:px-6 py-4 border-b border-edge bg-surface">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        {title && (
                          <DialogTitle className="text-base sm:text-lg font-semibold text-content">
                            {title}
                          </DialogTitle>
                        )}
                      </div>
                      {dismissible && (
                        <button
                          type="button"
                          onClick={saving ? undefined : onClose}
                          disabled={saving}
                          className={`shrink-0 p-2 -m-2 rounded-lg text-content-muted hover:text-content hover:bg-surface-2 transition-colors ${saving ? 'opacity-50 pointer-events-none' : ''}`}
                          aria-label={t('cm.close')}
                        >
                          <FiX size={18} />
                        </button>
                      )}
                    </div>
                  </div>
                )}

                <fieldset disabled={saving} aria-busy={saving} className="contents border-0 p-0 m-0 min-w-0">
                  {/* Content Area */}
                  <div className="grow overflow-y-auto px-4 sm:px-6 py-4">
                    {children}
                  </div>

                  {/* Sticky Footer */}
                  {footer && (
                    <div className="shrink-0 flex flex-col-reverse sm:flex-row items-stretch sm:items-center sm:justify-end gap-2 sm:gap-3 px-4 sm:px-6 py-3 sm:py-4 border-t border-edge bg-surface-2/50">
                      {footer}
                    </div>
                  )}
                </fieldset>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
